import React, { useState, useEffect } from 'react';
import {
    Input,
    Card,
    Collapse,
    Typography,
    Row,
    Col,
    Button,
    Space,
    Tag,
    Divider,
    message,
} from 'antd';
import {
    SearchOutlined,
    QuestionCircleOutlined,
    PhoneOutlined,
    MailOutlined,
    MessageOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;
const { Panel } = Collapse;

function HelpCenter() {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredFAQs, setFilteredFAQs] = useState([]);

    const faqCategories = [
        {
            key: 'general',
            title: 'General Questions',
            icon: '❓',
            faqs: [
                {
                    question: 'What is Spoon & Spice?',
                    answer: 'Spoon & Spice is India\'s premier catering platform that connects customers with verified, professional caterers for their special occasions. We provide a seamless booking experience with real-time pricing and quality guarantees.',
                },
                {
                    question: 'How do I place an order?',
                    answer: 'Simply select your city, occasion, date, and services on our homepage. You\'ll get real-time pricing from verified caterers in your area. Choose your preferred vendor and complete the booking process.',
                },
                {
                    question: 'What cities do you serve?',
                    answer: 'We currently serve major cities across India including Mumbai, Delhi, Bangalore, Chennai, Hyderabad, Pune, Kolkata, and many more. Check our homepage to see if we serve your city.',
                },
                {
                    question: 'Is there a minimum order amount?',
                    answer: 'Minimum order amounts vary by vendor and city. Most vendors have a minimum order of ₹5,000-₹10,000. You can see the minimum order amount for each vendor on their profile page.',
                },
            ],
        },
        {
            key: 'booking',
            title: 'Booking & Orders',
            icon: '📅',
            faqs: [
                {
                    question: 'How far in advance should I book?',
                    answer: 'We recommend booking at least 7-14 days in advance for the best vendor availability. For popular dates and large events, booking 3-4 weeks in advance is ideal.',
                },
                {
                    question: 'Can I modify my booking?',
                    answer: 'Yes, you can modify your booking up to 48 hours before the event date. Changes to guest count, menu items, or services may affect pricing. Contact our support team for assistance.',
                },
                {
                    question: 'What if I need to cancel my booking?',
                    answer: 'Cancellation policies vary by vendor. Generally, cancellations made 48+ hours in advance receive a full refund, 24-48 hours receive 50% refund, and less than 24 hours receive no refund.',
                },
                {
                    question: 'How do I track my order?',
                    answer: 'Once your booking is confirmed, you\'ll receive a confirmation email with tracking details. You can also track your order status in your account dashboard.',
                },
            ],
        },
        {
            key: 'payment',
            title: 'Payment & Pricing',
            icon: '💳',
            faqs: [
                {
                    question: 'What payment methods do you accept?',
                    answer: 'We accept all major credit/debit cards, UPI, net banking, and digital wallets like Paytm, PhonePe, and Google Pay. Cash on delivery is also available for select vendors.',
                },
                {
                    question: 'When do I need to pay?',
                    answer: 'Payment terms vary by vendor. Some require full payment upfront, while others accept partial payment with the balance due on delivery. Check the payment terms during booking.',
                },
                {
                    question: 'Are there any hidden charges?',
                    answer: 'No hidden charges! The price shown during booking includes all applicable taxes and service charges. Any additional fees (like delivery charges) will be clearly mentioned.',
                },
                {
                    question: 'Do you offer refunds?',
                    answer: 'Yes, we offer refunds according to our cancellation policy. Refunds are processed within 5-7 business days to your original payment method.',
                },
            ],
        },
        {
            key: 'delivery',
            title: 'Delivery & Service',
            icon: '🚚',
            faqs: [
                {
                    question: 'Do you provide delivery service?',
                    answer: 'Yes, most of our vendors provide delivery service. Delivery charges and minimum order amounts vary by vendor and location. Check vendor details for specific delivery information.',
                },
                {
                    question: 'What if my food arrives late?',
                    answer: 'We understand the importance of timely delivery. If your food arrives significantly late, please contact our support team immediately. We\'ll work with the vendor to resolve the issue.',
                },
                {
                    question: 'Do you provide setup and serving staff?',
                    answer: 'Many vendors offer setup and serving staff as additional services. You can request these services during booking. Charges for additional staff are clearly mentioned.',
                },
                {
                    question: 'What if the food quality is not as expected?',
                    answer: 'We take food quality seriously. If you\'re not satisfied with the food quality, please contact our support team within 2 hours of delivery. We\'ll investigate and provide appropriate resolution.',
                },
            ],
        },
        {
            key: 'vendor',
            title: 'Vendor Information',
            icon: '🏪',
            faqs: [
                {
                    question: 'How do you verify vendors?',
                    answer: 'All vendors go through a rigorous verification process including license verification, food safety certifications, customer reviews, and quality checks. We regularly audit our vendor partners.',
                },
                {
                    question: 'Can I see vendor reviews and ratings?',
                    answer: 'Yes, you can see detailed reviews and ratings for each vendor on their profile page. Reviews are from verified customers who have used the vendor\'s services.',
                },
                {
                    question: 'What if I want to become a vendor?',
                    answer: 'We\'re always looking for quality catering partners! Visit our vendor registration page or contact our vendor relations team at vendors@spoonandspice.com.',
                },
                {
                    question: 'How do I report an issue with a vendor?',
                    answer: 'You can report issues through our support channels or by calling our customer service number. We take all complaints seriously and will investigate promptly.',
                },
            ],
        },
    ];

    const contactMethods = [
        {
            icon: <PhoneOutlined className="text-2xl text-blue-600" />,
            title: 'Call Us',
            description: 'Speak with our support team',
            contact: '+91 7483423925',
            action: 'Call Now',
            href: 'tel:+917483423925',
        },
        {
            icon: <MailOutlined className="text-2xl text-green-600" />,
            title: 'Email Us',
            description: 'Send us your questions',
            contact: 'support@spoonandspice.com',
            action: 'Send Email',
            href: 'mailto:support@spoonandspice.com',
        },
        {
            icon: <MessageOutlined className="text-2xl text-purple-600" />,
            title: 'Live Chat',
            description: 'Chat with us instantly',
            contact: 'Available 9 AM - 9 PM',
            action: 'Start Chat',
            href: '#',
        },
    ];

    useEffect(() => {
        if (searchTerm) {
            const filtered = faqCategories.map(category => ({
                ...category,
                faqs: category.faqs.filter(faq =>
                    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
                ),
            })).filter(category => category.faqs.length > 0);
            setFilteredFAQs(filtered);
        } else {
            setFilteredFAQs(faqCategories);
        }
    }, [searchTerm]);

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <Title level={1} className="!text-white !mb-4">
                            Help Center
                        </Title>
                        <Paragraph className="!text-blue-100 !text-lg !mb-8 max-w-2xl mx-auto">
                            Find answers to your questions or get in touch with our support team
                        </Paragraph>
                        
                        <div className="max-w-2xl mx-auto">
                            <Search
                                placeholder="Search for help articles, FAQs, or topics..."
                                allowClear
                                size="large"
                                onSearch={handleSearch}
                                onChange={(e) => handleSearch(e.target.value)}
                                prefix={<SearchOutlined />}
                                className="mb-8"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Quick Contact */}
                <Card className="mb-12">
                    <Title level={3} className="!mb-6 text-center">
                        Need Immediate Help?
                    </Title>
                    <Row gutter={[24, 24]}>
                        {contactMethods.map((method, index) => (
                            <Col xs={24} md={8} key={index}>
                                <div className="text-center p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                    <div className="mb-4">{method.icon}</div>
                                    <Title level={4} className="!mb-2">{method.title}</Title>
                                    <Paragraph className="!mb-2 text-gray-600">{method.description}</Paragraph>
                                    <Text strong className="block mb-4">{method.contact}</Text>
                                    <Button type="primary" href={method.href}>
                                        {method.action}
                                    </Button>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>

                {/* FAQ Sections */}
                <div className="mb-8">
                    <Title level={2} className="!mb-2">
                        Frequently Asked Questions
                    </Title>
                    <Paragraph className="!text-gray-600">
                        Browse through our comprehensive FAQ sections to find answers to common questions
                    </Paragraph>
                </div>

                {filteredFAQs.length > 0 ? (
                    filteredFAQs.map((category) => (
                        <Card key={category.key} className="mb-6">
                            <div className="mb-4">
                                <Title level={3} className="!mb-2">
                                    <span className="mr-2">{category.icon}</span>
                                    {category.title}
                                </Title>
                                <Tag color="blue">{category.faqs.length} questions</Tag>
                            </div>
                            
                            <Collapse
                                accordion
                                size="large"
                                className="faq-collapse"
                            >
                                {category.faqs.map((faq, index) => (
                                    <Panel
                                        header={
                                            <div className="font-medium text-gray-800">
                                                {faq.question}
                                            </div>
                                        }
                                        key={`${category.key}-${index}`}
                                    >
                                        <Paragraph className="!mb-0 text-gray-700">
                                            {faq.answer}
                                        </Paragraph>
                                    </Panel>
                                ))}
                            </Collapse>
                        </Card>
                    ))
                ) : (
                    <Card className="text-center py-12">
                        <QuestionCircleOutlined className="text-6xl text-gray-400 mb-4" />
                        <Title level={3} className="!mb-2">No results found</Title>
                        <Paragraph className="!text-gray-600 !mb-6">
                            We couldn't find any FAQs matching your search. Try different keywords or contact our support team.
                        </Paragraph>
                        <Space>
                            <Button onClick={() => setSearchTerm('')}>Clear Search</Button>
                            <Button type="primary" href="mailto:support@spoonandspice.com">
                                Contact Support
                            </Button>
                        </Space>
                    </Card>
                )}

                {/* Additional Resources */}
                <Card className="mt-12">
                    <Title level={3} className="!mb-6 text-center">
                        Additional Resources
                    </Title>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={8}>
                            <div className="text-center p-6">
                                <ClockCircleOutlined className="text-4xl text-blue-600 mb-4" />
                                <Title level={4} className="!mb-2">Business Hours</Title>
                                <Paragraph className="!text-gray-600">
                                    Monday - Friday: 9:00 AM - 9:00 PM<br />
                                    Saturday - Sunday: 10:00 AM - 8:00 PM
                                </Paragraph>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="text-center p-6">
                                <MessageOutlined className="text-4xl text-green-600 mb-4" />
                                <Title level={4} className="!mb-2">Response Time</Title>
                                <Paragraph className="!text-gray-600">
                                    Email: Within 2 hours<br />
                                    Phone: Immediate<br />
                                    Live Chat: Within 5 minutes
                                </Paragraph>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="text-center p-6">
                                <QuestionCircleOutlined className="text-4xl text-purple-600 mb-4" />
                                <Title level={4} className="!mb-2">Still Need Help?</Title>
                                <Paragraph className="!text-gray-600 !mb-4">
                                    Can't find what you're looking for?
                                </Paragraph>
                                <Button type="primary" href="/contact">
                                    Contact Us
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </div>
        </div>
    );
}

export default HelpCenter;
